// -----------------------------------------------------------------------------
// reportHelpers.ts
//
// Pure, framework-free helpers for the Reporting screen. Kept out of the React
// component so the logic (column derivation, chart shaping) is easy to reason
// about and unit-test (see AGENTS.md: extract logic into helpers).
// -----------------------------------------------------------------------------

import { BuilderState, FieldAggregate, fieldKey, isJoinComplete } from "./reportQuery";

/** Chart shapes the demo can render. Column..Scatter are Cartesian; Pie/Donut use the accumulation chart. */
export type ChartKind =
  | "Column"
  | "Bar"
  | "Line"
  | "Area"
  | "Spline"
  | "Scatter"
  | "Pie"
  | "Donut";

export const CARTESIAN_CHARTS: ChartKind[] = ["Column", "Bar", "Line", "Area", "Spline", "Scatter"];

export function isAccumulationChart(kind: ChartKind): boolean {
  return kind === "Pie" || kind === "Donut";
}

/**
 * The Syncfusion grid column `type`. Derived from the sampled values' real JS
 * types (see {@link DerivedColumn.gridType}) — never from what the values look
 * like, and never left for the grid to infer.
 */
export type GridColumnType = "number" | "boolean" | "date" | "datetime" | "string";

export interface DerivedColumn {
  field: string;
  headerText: string;
  /**
   * Every non-blank sampled value is a real JS `number`. This is the ONLY flag the
   * grid may type a column by (`type="number"`), because the JSON the BFF returns
   * carries the column's SQL type: a numeric SQL column arrives as a JSON number,
   * a `varchar` arrives as a string — even when it happens to hold digits.
   *
   * Drives the column's alignment; {@link gridType} carries it to the grid's `type`.
   */
  isNumeric: boolean;
  /**
   * The `type` to hand the grid, derived from the JS types across the WHOLE sample
   * and always set — the grid must never be left to infer it. Syncfusion infers a
   * column's type from **row 0 only** (`Render.updateColumnType`), once per grid
   * (`isColTypeDef`), and when row 0 holds `null` it pins the type to `null`
   * instead — after which `renderFlValueUI` returns early and the filter menu opens
   * with an operator dropdown and **no value box at all**. A nullable text column
   * (a `CompletedBy` on a job nobody has completed yet) is exactly that case.
   *
   * A column of ISO date STRINGS is `"string"`, deliberately: the row holds a
   * string, and a date filter would compare the `Date` it produces against it and
   * match nothing — the same mistake as typing numeric text as a number.
   */
  gridType: GridColumnType;
  /**
   * The values parse as numbers, whether they arrived as numbers or as strings.
   * Charting only (see {@link buildAutoChart}) — deliberately NOT a grid type, or
   * a text code like a "020" CompletedBy would be filtered as the number 20.
   */
  looksNumeric: boolean;
  isDate: boolean;
  /** Auto-fit pixel width based on header + sampled content (so columns aren't cut off). */
  width: number;
}

export type JoinType = "inner" | "left";

/** One output column of an aggregate query, tagged with its role so the chart
 *  knows what to use as a category (dimension) vs a series (measure). */
export interface AggregateColumn {
  /** The key as it appears in the returned rows (mirrors the server SELECT alias). */
  key: string;
  label: string;
  role: "dimension" | "measure";
}

/** One chart series (one per measure). `key` matches a column in the point rows. */
export interface ChartSeries {
  key: string;
  label: string;
}

/** A fully query-driven chart: one series per measure, one point per result row,
 *  category built from all group-by dimensions. No independent chart controls. */
export interface MultiSeriesChart {
  points: Record<string, string | number>[];
  series: ChartSeries[];
  /** Axis title built from the dimension labels (e.g. "Warehouse / Status"). */
  categoryLabel: string;
}

// How many rows to sample when inferring column types. Keeps big result sets cheap.
const SAMPLE_SIZE = 100;
// Column auto-fit tuning (approximate px per character + padding, clamped).
const CHAR_PX = 8;
const CELL_PAD = 30;
const MIN_COL_WIDTH = 110;
const MAX_COL_WIDTH = 460;
// Cap the number of chart categories so a chart never turns into an unreadable smear.
const MAX_CHART_CATEGORIES = 50;

// Words that stay uppercase in a sentence-cased header even though the column
// spells them in mixed case ("poWarehouse" -> "PO warehouse"). A word the column
// already spells in full caps (WHOTO, SAP) is left alone regardless, so this list
// only has to cover the camelCase spellings.
const HEADER_ACRONYMS = new Set([
  "ATP", "BOM", "BP", "CNC", "CSM", "ID", "PO", "PW", "SAP", "SSC", "UOM",
]);

/**
 * "scheduledEndDate" / "scheduled_end_date" -> "Scheduled end date".
 * Sentence case, per the UI convention that headers capitalize only the first
 * word; recognized acronyms stay uppercase.
 */
export function humanizeKey(key: string): string {
  if (!key) return "";
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[_\-.]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ");

  return words
    .map((word, i) => {
      if (HEADER_ACRONYMS.has(word.toUpperCase())) return word.toUpperCase();
      // Already all caps in the schema (WHOTO, SAP): assume it's an initialism.
      if (word.length > 1 && word === word.toUpperCase() && /[A-Z]/.test(word)) return word;
      const lower = word.toLowerCase();
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(" ");
}

/** A value that IS a number, as opposed to one that reads like one ("020"). */
export function isRealNumber(v: unknown): boolean {
  return typeof v === "number" && Number.isFinite(v);
}

/** A value that can be read as a number, including numeric text. */
export function isNumericValue(v: unknown): boolean {
  if (typeof v === "number") return Number.isFinite(v);
  if (typeof v === "string") {
    const t = v.trim();
    if (t === "") return false;
    const n = Number(t.replace(/,/g, ""));
    return Number.isFinite(n);
  }
  return false;
}

/** Detects Date instances and leading-ISO date strings without parsing (keeps it deterministic). */
export function isDateValue(v: unknown): boolean {
  if (v instanceof Date) return true;
  if (typeof v === "string") return /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2})?/.test(v.trim());
  return false;
}

/** A value that IS a Date, as opposed to a string that reads like one. */
function isRealDate(v: unknown): boolean {
  return v instanceof Date && !Number.isNaN(v.getTime());
}

/** A Date carrying a time of day, i.e. `datetime` rather than `date`. */
function hasTimeOfDay(v: Date): boolean {
  return (
    v.getHours() > 0 || v.getMinutes() > 0 || v.getSeconds() > 0 || v.getMilliseconds() > 0
  );
}

/** Coerce a cell to a number for aggregation. Non-numeric -> 0. */
export function toNumber(v: unknown): number {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const n = Number(v.trim().replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function formatCategory(v: unknown): string {
  if (v === null || v === undefined || v === "") return "(blank)";
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v);
}

function cellToString(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

/** Approximate an Excel-style auto-fit width from the header and sampled cell values. */
export function computeColumnWidth(rows: any[], field: string, headerText: string): number {
  let maxLen = (headerText || field).length;
  const sample = rows.slice(0, SAMPLE_SIZE);
  for (const row of sample) {
    const len = cellToString(row?.[field]).length;
    if (len > maxLen) maxLen = len;
  }
  const px = Math.round(maxLen * CHAR_PX + CELL_PAD);
  return Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, px));
}

/** Make a safe object-key prefix (no dots — Syncfusion treats dots as nested paths). */
export function slugify(s: string): string {
  return (s || "").replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "") || "col";
}

function joinKey(v: unknown): string {
  return String(v ?? "").trim().toLowerCase();
}

/**
 * Client-side join of two already-loaded, already-authorized result sets.
 * Right-hand columns are prefixed (with `rightPrefix_`) so they never clash with
 * left columns. `inner` keeps only matched left rows; `left` keeps all left rows.
 */
export function joinRows(
  left: any[],
  right: any[],
  leftKey: string,
  rightKey: string,
  type: JoinType,
  rightPrefix: string
): any[] {
  if (!left) return [];
  if (!leftKey || !rightKey) return left;

  const index = new Map<string, any[]>();
  for (const r of right || []) {
    const k = joinKey(r?.[rightKey]);
    const bucket = index.get(k);
    if (bucket) bucket.push(r);
    else index.set(k, [r]);
  }

  const prefix = `${slugify(rightPrefix)}_`;
  const withPrefix = (r: any) => {
    const out: Record<string, any> = {};
    for (const k of Object.keys(r || {})) out[prefix + k] = r[k];
    return out;
  };

  const result: any[] = [];
  for (const l of left) {
    const matches = index.get(joinKey(l?.[leftKey]));
    if (matches && matches.length) {
      for (const m of matches) result.push({ ...l, ...withPrefix(m) });
    } else if (type === "left") {
      result.push({ ...l });
    }
  }
  return result;
}

/**
 * Infer displayable columns from result rows: preserves first-seen key order,
 * humanizes headers, and flags numeric/date columns.
 *
 * Two numeric flags, and the difference matters: `isNumeric` requires every
 * non-blank sampled value to be a real JS `number` (i.e. the SQL column really is
 * numeric — that is what the grid types a column by), while `looksNumeric` also
 * accepts numeric text and exists only so the chart can still measure a column
 * whose numbers arrive as strings. Typing a column of numeric TEXT as a number is
 * the bug this split fixes: the value the grid holds is the string, so a numeric
 * filter for `020` searches for the number 20 and matches nothing.
 *
 * `gridType` is what the grid is actually given, and it is ALWAYS set — see the
 * field's doc for why leaving it to Syncfusion loses the filter box outright on a
 * column whose first row is null.
 *
 * `headerLabels` is matched CASE-INSENSITIVELY, and that is load-bearing rather
 * than defensive: the BFF's MVC output formatter is Newtonsoft with
 * `CamelCasePropertyNamesContractResolver` (Bootstrapper.AddJsonOptions), whose
 * naming strategy has `ProcessDictionaryKeys = true` — and a report row IS a
 * dictionary (Dapper's `Query<object>` row). So the JSON that reaches us has
 * camelCased KEYS: `AvailHours.Machine` arrives as `machine`, a joined
 * `AvailHours_Machine` as `availHours_Machine`, a measure `Sum_AvailHours` as
 * `sum_AvailHours`. The labels are keyed by the real, schema-cased column name
 * (that is what the builder and the saved definition speak), so an exact lookup
 * missed every column and every heading silently fell back to `humanizeKey` —
 * which is why the results grid and the chart showed "Machine" while the builder
 * showed "User". The camelCasing only ever changes CASE, never characters, so a
 * case-insensitive match inverts it exactly.
 */
export function deriveColumns(rows: any[], headerLabels?: Record<string, string>): DerivedColumn[] {
  if (!rows || rows.length === 0) return [];
  const sample = rows.slice(0, SAMPLE_SIZE);

  // Exact keys win; the lowercase index is the fallback (see above). Built once
  // per call, and first-write-wins so two labels differing only in case resolve
  // to the one buildHeaderLabels emitted first — i.e. the query's own order.
  const byLowerKey = new Map<string, string>();
  for (const [key, label] of Object.entries(headerLabels ?? {})) {
    const k = key.toLowerCase();
    if (!byLowerKey.has(k)) byLowerKey.set(k, label);
  }

  const keys: string[] = [];
  const seen = new Set<string>();
  for (const row of sample) {
    if (row && typeof row === "object" && !Array.isArray(row)) {
      for (const k of Object.keys(row)) {
        if (!seen.has(k)) {
          seen.add(k);
          keys.push(k);
        }
      }
    }
  }

  return keys.map((field) => {
    let nonNull = 0;
    let realNumbers = 0;
    let numeric = 0;
    let dates = 0;
    let realDates = 0;
    let timedDates = 0;
    let booleans = 0;
    for (const row of sample) {
      const v = row?.[field];
      if (v === null || v === undefined || v === "") continue;
      nonNull++;
      if (isRealNumber(v)) realNumbers++;
      if (isNumericValue(v)) numeric++;
      if (isDateValue(v)) dates++;
      if (isRealDate(v)) {
        realDates++;
        if (hasTimeOfDay(v as Date)) timedDates++;
      }
      if (typeof v === "boolean") booleans++;
    }
    const isNumeric = nonNull > 0 && realNumbers === nonNull;
    const looksNumeric = nonNull > 0 && numeric === nonNull;
    const isDate = !looksNumeric && nonNull > 0 && dates === nonNull;
    // Every column gets a type, including one the sample saw nothing but blanks in
    // — "string" is both the safe default (its filter reads the cell as text, which
    // any value survives) and the only one that guarantees a value box. Order
    // matters only in that each branch demands the WHOLE sample be that JS type.
    const gridType: GridColumnType = isNumeric
      ? "number"
      : nonNull > 0 && booleans === nonNull
        ? "boolean"
        : nonNull > 0 && realDates === nonNull
          ? timedDates > 0
            ? "datetime"
            : "date"
          : "string";
    // The row key is all this function sees, and it is not always a bare column
    // name (a joined column arrives as "<Table>_<col>", a measure as "<Agg>_<col>",
    // and every one of them is camelCased on the wire), so a caller that knows the
    // query it ran passes the labels in — buildHeaderLabels below builds them.
    // Anything unmapped still humanizes.
    const headerText =
      headerLabels?.[field] ?? byLowerKey.get(field.toLowerCase()) ?? humanizeKey(field);
    return {
      field,
      headerText,
      isNumeric,
      gridType,
      looksNumeric,
      isDate,
      width: computeColumnWidth(sample, field, headerText),
    };
  });
}

/**
 * The columns an aggregate query will return, in SELECT order, each tagged as a
 * dimension (group-by) or a measure (summarized field). This MIRRORS the server
 * SELECT aliasing in CSM.Shared.DataQuery.ReportSqlBuilder so the chart can read
 * the exact keys the rows carry — keep the two in sync:
 *   - group-by dimension: primary column as-is, joined column "<Table>_<col>";
 *   - measure: "<Agg>_<col>" (primary) or "<Agg>_<Table>_<col>" (joined);
 *   - when there are dimensions but no measures, an implicit "Count".
 * Returns [] when the query isn't an aggregate (no group-by and no measures).
 */
export function aggregateOutputColumns(
  state: BuilderState,
  labelFor: (table: string, field: string) => string = (_t, f) => humanizeKey(f)
): AggregateColumn[] {
  const primary = state.table;
  const isPrimary = (t?: string) => !t || t === primary;
  const aggregates = state.aggregates ?? {};

  const dimensions = (state.groupBy ?? []).filter((g) => g.field && g.table);

  // Mirrors buildQueryRequest: measures follow the display order of the selected
  // fields, and only tables that would actually be sent (the primary plus every
  // complete join) contribute columns.
  const inPlay = new Set<string>([
    primary,
    ...(state.joins ?? []).filter(isJoinComplete).map((j) => j.table),
  ]);
  const measures: { table: string; field: string; aggregate: FieldAggregate }[] = [];
  const seen = new Set<string>();
  for (const f of state.select ?? []) {
    if (!f.field || !f.table || !inPlay.has(f.table)) continue;
    const key = fieldKey(f.table, f.field);
    if (seen.has(key)) continue;
    seen.add(key);
    const aggregate = aggregates[key];
    if (aggregate && aggregate !== "None") measures.push({ table: f.table, field: f.field, aggregate });
  }

  if (dimensions.length === 0 && measures.length === 0) return [];

  const cols: AggregateColumn[] = [];
  for (const d of dimensions) {
    const key = isPrimary(d.table) ? d.field : `${d.table}_${d.field}`;
    cols.push({ key, label: labelFor(d.table, d.field), role: "dimension" });
  }
  for (const m of measures) {
    const key = isPrimary(m.table)
      ? `${m.aggregate}_${m.field}`
      : `${m.aggregate}_${m.table}_${m.field}`;
    cols.push({ key, label: `${m.aggregate} of ${labelFor(m.table, m.field)}`, role: "measure" });
  }
  if (measures.length === 0) {
    cols.push({ key: "Count", label: "Count", role: "measure" });
  }
  return cols;
}

/**
 * Row key → column heading, for the query `state` describes. This is the bridge
 * between the labels (which are keyed by table + column) and the result rows
 * (which carry only the server's SELECT key), and it MIRRORS the same aliasing
 * `aggregateOutputColumns` documents above — primary columns unprefixed, joined
 * columns "<Table>_<col>", measures "<Agg>_<col>".
 *
 * Pass the result to {@link deriveColumns}. Keys it doesn't cover fall back to
 * `humanizeKey`, so an unexpected shape degrades to today's behaviour rather than
 * to a blank header.
 *
 * `columnsByTable` is the loaded schema (table → its columns) and covers the one
 * output shape the builder state alone can't describe: choosing NO column, which
 * the BFF answers with every column of every table in the query. Without it that
 * query — the one returning the most columns — is the only one that shows none of
 * the labels. Optional, so a caller without the schema keeps the old behaviour.
 */
export function buildHeaderLabels(
  state: BuilderState,
  labelFor: (table: string, field: string) => string = (_t, f) => humanizeKey(f),
  columnsByTable?: Record<string, readonly { name: string }[] | undefined>
): Record<string, string> {
  const labels: Record<string, string> = {};

  // Aggregate query: the dimensions and measures ARE the output.
  const aggregated = aggregateOutputColumns(state, labelFor);
  if (aggregated.length > 0) {
    for (const col of aggregated) labels[col.key] = col.label;
    return labels;
  }

  // Detail query: the selected fields, in display order. Same in-play/dedupe
  // rules as aggregateOutputColumns, for the same reason — a field on an
  // incomplete join is not sent, so it returns no column.
  const primary = state.table;
  const joined = (state.joins ?? []).filter(isJoinComplete).map((j) => j.table);
  const inPlay = new Set<string>([primary, ...joined]);
  let chosen = 0;
  for (const f of state.select ?? []) {
    if (!f.field || !f.table || !inPlay.has(f.table)) continue;
    chosen++;
    const key = f.table === primary ? f.field : `${f.table}_${f.field}`;
    if (labels[key] === undefined) labels[key] = labelFor(f.table, f.field);
  }
  if (chosen > 0) return labels;

  // Nothing chosen (the builder says so: "every column of every table in the
  // query will be shown"). ReportSqlBuilder then takes its legacy branch and
  // emits the primary table's whole column list followed by each joined table's,
  // aliased exactly as above — so label the schema the same way.
  for (const table of [primary, ...joined]) {
    for (const col of columnsByTable?.[table] ?? []) {
      const key = table === primary ? col.name : `${table}_${col.name}`;
      if (labels[key] === undefined) labels[key] = labelFor(table, col.name);
    }
  }
  return labels;
}

/**
 * Shape the query's already-aggregated rows into a multi-series chart driven
 * entirely by the built query: one series per measure, one point per result row,
 * with the category built from ALL group-by dimensions ("dimA / dimB"). Does NOT
 * re-aggregate; sorts by the first measure descending and caps the point count.
 */
export function buildMultiSeriesChart(rows: any[], cols: AggregateColumn[]): MultiSeriesChart {
  const dimensions = cols.filter((c) => c.role === "dimension");
  const measures = cols.filter((c) => c.role === "measure");
  const series: ChartSeries[] = measures.map((m) => ({ key: m.key, label: m.label }));
  const categoryLabel = dimensions.map((d) => d.label).join(" / ") || "Total";

  if (!rows || rows.length === 0 || measures.length === 0) {
    return { points: [], series, categoryLabel };
  }

  const points: Record<string, string | number>[] = rows.map((row) => {
    const category =
      dimensions.length > 0
        ? dimensions.map((d) => formatCategory(row?.[d.key])).join(" / ")
        : "Total";
    const point: Record<string, string | number> = { category };
    for (const m of measures) point[m.key] = toNumber(row?.[m.key]);
    return point;
  });

  const firstKey = measures[0].key;
  points.sort((a, b) => (b[firstKey] as number) - (a[firstKey] as number));
  return {
    points: points.length > MAX_CHART_CATEGORIES ? points.slice(0, MAX_CHART_CATEGORIES) : points,
    series,
    categoryLabel,
  };
}

/**
 * Build a chart from ANY executed result set — not only aggregate queries — so the
 * chart "follows the query": run anything, switch to the Chart view, and see it
 * plotted. The first non-numeric column (a label/date) becomes the category
 * (x-axis); every remaining numeric column becomes a series. Row order from the
 * query is preserved (so a date/sequence charts naturally) and the point count is
 * capped so a large result never turns into an unreadable smear.
 *
 * Returns empty series/points (so the page can show a hint) when there is nothing
 * to plot — no rows, or no numeric column to measure. Takes the already-derived
 * columns (see {@link deriveColumns}) so numeric/date detection isn't duplicated.
 */
export function buildAutoChart(rows: any[], columns: DerivedColumn[]): MultiSeriesChart {
  if (!rows || rows.length === 0 || columns.length === 0) {
    return { points: [], series: [], categoryLabel: "" };
  }

  // Category: the first non-numeric column (string or date). If every column is
  // numeric there's no natural label, so fall back to the row's position.
  // Both sides read `looksNumeric`, not `isNumeric`: a measure is still a measure
  // when its numbers arrive as strings. (The grid types columns by `isNumeric`
  // instead — see DerivedColumn.)
  const categoryCol = columns.find((c) => !c.looksNumeric) ?? null;
  const measures = columns.filter((c) => c.looksNumeric && c.field !== categoryCol?.field);
  if (measures.length === 0) {
    return { points: [], series: [], categoryLabel: categoryCol?.headerText ?? "" };
  }

  const series: ChartSeries[] = measures.map((m) => ({ key: m.field, label: m.headerText }));
  const categoryLabel = categoryCol?.headerText ?? "Row";

  const capped = rows.length > MAX_CHART_CATEGORIES ? rows.slice(0, MAX_CHART_CATEGORIES) : rows;
  const points: Record<string, string | number>[] = capped.map((row, i) => {
    const category = categoryCol ? formatCategory(row?.[categoryCol.field]) : `Row ${i + 1}`;
    const point: Record<string, string | number> = { category };
    for (const m of measures) point[m.field] = toNumber(row?.[m.field]);
    return point;
  });

  return { points, series, categoryLabel };
}
